import React, { useEffect, useMemo, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import LoadThemeComponent from '../../../../base/LoadThemeComponent';
import trackQuickPickButtonClicked from '../../../../base/trackers/helpers';
import { isThankYouLotto } from '../../../../utils/template';
import clsx from '../../../Widgets/Utils/clsx';
import LinePrice from '../../Pick/basket/LinePrice';
import { LineProps, SelectSignalLottery } from '../../Types/Types';
import {
    PRIMARY_NUMBERS,
    BONUS_NUMBERS,
    RANGE_FROM,
    RANGE_TO,
    isValidLine,
    toggleNumber,
} from '../../helpers/linesHelper';
import useLinesHelper from '../../helpers/useLinesHelper';
import { RootState } from '../../redux/store';

import NumberPanelTitle from './NumberPanelTitle';

import DivButton from 'components/common/DivButton';
import { rangeArray } from 'utils/helpers';

type Props = Readonly<{
    selectSignal?: SelectSignalLottery;
}> &
    LineProps;

export default function NumberPanel({ selectSignal, lineNumbers, lineIndex, stake }: Props) {
    const { t } = useTranslation();
    const { lottery, isMobile, stakeMin, stakeMax, linePriceGross } = useSelector(
        (state: RootState) => state.playStore
    );
    const {
        ranges,
        numbersPerLine,
        setLine,
        setLineStake,
        setEmptyLine,
        setRandomLine,
        isBonusNumberPickable,
        triggerChooseLotteryMode,
        setCurrentPickerIndex,
    } = useLinesHelper();

    const isMegaJackpot = lottery?.display_slug?.startsWith('mega-jackpot');
    const isPermsModeAvailable = lottery?.lotteryRule?.is_perms_enabled;

    const isTemplateThankYouLotto = useMemo(() => isThankYouLotto(), []);

    const [showModal, setShow] = useState<boolean>();

    useEffect(() => {
        setShow(selectSignal?.isInitialized);
    }, [selectSignal]);

    // eslint-disable-next-line no-param-reassign
    lineNumbers = isBonusNumberPickable(lottery) ? lineNumbers : [lineNumbers[0]];

    const handleBallClick = (targetNumber: number, numbersIndex: number) => {
        if (lineNumbers.length <= numbersIndex) {
            throw new Error('Incorrect line structure.');
        }

        if (isPermsModeAvailable) {
            if (
                lineNumbers[PRIMARY_NUMBERS].length === numbersPerLine[PRIMARY_NUMBERS] &&
                !lineNumbers[PRIMARY_NUMBERS].includes(targetNumber)
            ) {
                triggerChooseLotteryMode(true);
            }
        }

        const newLineNumbers = lineNumbers.map((numbers, index) =>
            index === numbersIndex ? toggleNumber(numbers, targetNumber, numbersPerLine[numbersIndex]) : numbers
        );
        setCurrentPickerIndex(lineIndex);

        setLine(lineIndex, newLineNumbers);
    };

    const checked = isValidLine(lottery, lineNumbers, numbersPerLine);

    const baseView = () => {
        function titlePanel() {
            if (isMobile || isTemplateThankYouLotto) {
                return null;
            }

            return (
                <>
                    <p className="font-bold text-center text-pick">
                        <NumberPanelTitle />
                    </p>
                    {checked && <p className="font-bold text-center text-ok">{t('OK')}</p>}
                </>
            );
        }

        function buttonsPanel() {
            if (isTemplateThankYouLotto) {
                return (
                    <div className="d-flex justify-content-between align-items-center">
                        {/* TODO: add active class "active" + "bi-check" icon */}
                        <button
                            type="button"
                            className="btn lucky-dip-btn"
                            onClick={() => {
                                setRandomLine(lineIndex);
                                trackQuickPickButtonClicked(lottery.slug);
                            }}
                        >
                            {/* TODO: Add translation */}
                            Lucky Dip
                        </button>
                        {/* TODO: add active class "bg-white text-primary" */}
                        <button type="button" className="btn trash-btn" onClick={() => setEmptyLine(lineIndex)}>
                            <i className="bi bi-trash" />
                        </button>
                    </div>
                );
            }

            return (
                <div className="row row-buttons no-gutters d-flex justify-content-between">
                    <div>
                        <button
                            type="button"
                            onClick={() => setEmptyLine(lineIndex)}
                            className="btn btn-secondary purchase-line-clear"
                        >
                            {t('playPicker.clear')}
                        </button>
                    </div>
                    <div>
                        <button
                            type="button"
                            onClick={() => {
                                setRandomLine(lineIndex);
                                trackQuickPickButtonClicked(lottery.slug);
                            }}
                            className="btn btn-primary quick-pick"
                        >
                            {t('playPicker.quickPick')}
                        </button>
                    </div>
                </div>
            );
        }

        function primaryLines() {
            function lines() {
                const numberActiveClass = isTemplateThankYouLotto ? 'bg-info' : 'choosed';
                return rangeArray(ranges[PRIMARY_NUMBERS][RANGE_FROM], ranges[PRIMARY_NUMBERS][RANGE_TO]).map((n) => (
                    <DivButton
                        key={n}
                        onClick={() => handleBallClick(n, PRIMARY_NUMBERS)}
                        className={clsx(
                            isTemplateThankYouLotto
                                ? 'lotto-ball'
                                : 'number text-center purchase-line-number purchase-line-chooseable',
                            lineNumbers[PRIMARY_NUMBERS]?.includes(n) ? numberActiveClass : ''
                        )}
                    >
                        {n}
                    </DivButton>
                ));
            }

            if (isTemplateThankYouLotto) {
                return (
                    <div className="mt-2 text-center">
                        <div className="lotto-grid lotto-number-grid">{lines()}</div>
                    </div>
                );
            }

            return <div className="row row-numbers no-gutters purchase-line-numbers">{lines()}</div>;
        }

        function bonusLinesTitle() {
            if (numbersPerLine[BONUS_NUMBERS] <= 0 || !isBonusNumberPickable(lottery)) {
                return null;
            }

            if (isTemplateThankYouLotto) {
                // TODO: Add translation
                return (
                    <div className="text-center text-white mt-3">
                        {t(`playPicker.lottery.${isMegaJackpot ? 'pickExtraNumber' : 'pickBonusNumber'}`, {
                            count: numbersPerLine[BONUS_NUMBERS],
                            name: lottery.bonusNumberName,
                        })}
                    </div>
                );
            }

            if (!isMobile) {
                return null;
            }

            return (
                <div className="row">
                    <h5 className="title text-center w-100">
                        {t(`playPicker.lottery.${isMegaJackpot ? 'pickExtraNumber' : 'pickBonusNumber'}`, {
                            count: numbersPerLine[BONUS_NUMBERS],
                            name: lottery.bonusNumberName,
                        })}
                    </h5>
                </div>
            );
        }

        function bonusLines() {
            if (numbersPerLine[BONUS_NUMBERS] <= 0 || !isBonusNumberPickable(lottery)) {
                return null;
            }

            const numberActiveClass = isTemplateThankYouLotto ? 'bg-info' : 'choosed';
            return (
                <div
                    className={
                        isTemplateThankYouLotto
                            ? 'lotto-grid lotto-star-grid mt-2'
                            : 'row row-numbers no-gutters purchase-line-bnumbers'
                    }
                >
                    {rangeArray(ranges[BONUS_NUMBERS]?.[RANGE_FROM], ranges[BONUS_NUMBERS]?.[RANGE_TO]).map((n) => (
                        <DivButton
                            key={n}
                            onClick={() => handleBallClick(n, BONUS_NUMBERS)}
                            className={clsx(
                                isTemplateThankYouLotto ? 'lotto-star' : 'number text-center purchase-line-bnumber',
                                lineNumbers[BONUS_NUMBERS]?.includes(n) ? numberActiveClass : ''
                            )}
                        >
                            {n}
                        </DivButton>
                    ))}
                </div>
            );
        }

        return (
            <div className={clsx(isTemplateThankYouLotto ? 'lotto-card' : 'play-picker', checked ? 'checked' : '')}>
                {titlePanel()}
                {buttonsPanel()}
                {isTemplateThankYouLotto && (
                    <div className="text-center text-white mt-3">
                        <NumberPanelTitle />
                    </div>
                )}
                <div className="purchase-line">
                    {primaryLines()}
                    {bonusLinesTitle()}
                    {bonusLines()}
                    <LinePrice
                        index={lineIndex}
                        stake={stake}
                        stakeMin={stakeMin}
                        stakeMax={stakeMax}
                        linePriceGross={linePriceGross}
                        setLineStake={setLineStake}
                        ticketPriceMin={null}
                        ticketPriceMax={null}
                    />
                </div>
            </div>
        );
    };

    const defaultView = () => (
        <Modal className="play-picker-modal" show={showModal} onHide={() => setShow(false)} centered>
            <Modal.Header closeButton />
            <Modal.Body>
                <div className={`play-picker ${checked ? 'checked' : ''}`}>
                    <div className="row-buttons no-gutters d-flex justify-content-center">
                        <div>
                            <button
                                type="button"
                                onClick={() => {
                                    setRandomLine(lineIndex);
                                    trackQuickPickButtonClicked(lottery.slug);
                                }}
                                className="btn btn-secondary quick-pick"
                            >
                                {t('playPicker.quickPick')}
                            </button>
                        </div>
                        <div>
                            <button
                                type="button"
                                onClick={() => setEmptyLine(lineIndex)}
                                className="btn btn-secondary purchase-line-clear"
                            >
                                {t('playPicker.clear')}
                            </button>
                        </div>
                    </div>
                    <div className="pick-info d-flex justify-content-center">
                        {lineNumbers[PRIMARY_NUMBERS].length === numbersPerLine[PRIMARY_NUMBERS] && (
                            <i className="fas fa-check-circle" />
                        )}

                        <span>{`Pick ${numbersPerLine[PRIMARY_NUMBERS]} numbers`}</span>
                    </div>
                    <div className="purchase-line">
                        <div className="purchase-line-numbers">
                            {rangeArray(ranges[PRIMARY_NUMBERS][RANGE_FROM], ranges[PRIMARY_NUMBERS][RANGE_TO]).map(
                                (n) => (
                                    <DivButton
                                        key={n}
                                        onClick={() => handleBallClick(n, PRIMARY_NUMBERS)}
                                        className={`number text-center purchase-line-number d-flex align-items-center justify-content-center purchase-line-to-pick ${
                                            lineNumbers[PRIMARY_NUMBERS]?.includes(n) ? 'picked' : ''
                                        }`}
                                    >
                                        <i className="fas fa-times" />
                                        {n}
                                    </DivButton>
                                ),
                            )}
                        </div>
                        {numbersPerLine[BONUS_NUMBERS] > 0 && isBonusNumberPickable(lottery) && (
                            <div className="pick-info d-flex justify-content-center">
                                {lineNumbers[BONUS_NUMBERS].length === numbersPerLine[BONUS_NUMBERS] && (
                                    <i className="fas fa-check-circle" />
                                )}
                                <span>
                                    {t(`playPicker.lottery.${isMegaJackpot ? 'pickExtraNumber' : 'pickBonusNumber'}`, {
                                        count: numbersPerLine[BONUS_NUMBERS],
                                        name: lottery.bonusNumberName,
                                    })}
                                </span>
                            </div>
                        )}
                        {numbersPerLine[BONUS_NUMBERS] > 0 && isBonusNumberPickable(lottery) && (
                            <div className="purchase-line-numbers bonus-numbers">
                                {rangeArray(ranges[BONUS_NUMBERS]?.[RANGE_FROM], ranges[BONUS_NUMBERS]?.[RANGE_TO]).map(
                                    (n) => (
                                        <DivButton
                                            key={n}
                                            onClick={() => handleBallClick(n, BONUS_NUMBERS)}
                                            className={`number text-center purchase-line-number purchase-line-to-pick  d-flex align-items-center justify-content-center ${
                                                lineNumbers[BONUS_NUMBERS]?.includes(n) ? 'picked' : ''
                                            }`}
                                        >
                                            <i className="fas fa-times" />
                                            {n}
                                        </DivButton>
                                    )
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </Modal.Body>
            <Modal.Footer>
                <button type="button" className="btn btn-primary mx-auto" onClick={() => setShow(false)}>
                    {t('playPicker.modal.done')}
                </button>
            </Modal.Footer>
        </Modal>
    );

    return <LoadThemeComponent defaultView={defaultView} baseView={baseView} />;
}
